import { CircularProgress, Box, Button } from '@mui/material';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Grid, GridCellProps, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { useCallback, useEffect, useState } from 'react';
import { post } from 'src/api/Api';
import VendorApi from 'src/api/VendorApi';
import useAuth from 'src/hooks/useAuth';
import useSnackbar from 'src/hooks/useSnackbar';
import useVendor from 'src/hooks/useVendor';
import { defaultEditVendorModal, EditVendorModal } from 'src/types/EditVendorModal';
import { User } from 'src/types/user';
import { Vendor } from 'src/types/vendor';
import DateCell from '../grid/DateCell';
import LinkVendorDialog from './linkVendorDialog';
import VendorEditForm from './vendorEditForm';
import ShowTour from 'src/components/ShowTour';

declare interface Props {}

const UserAdminGrid: React.FC<React.PropsWithChildren<Props>> = () => {
  const { vendor } = useVendor();
  const [sort, setSort] = useState<Array<SortDescriptor>>([{ field: 'fieldName', dir: 'asc' }]);
  const [data, setData] = useState<User[]>([]);
  const [openForm, setOpenForm] = useState(false);
  const [openLinkUser, setOpenLinkUser] = useState(false);
  const [editUser, setEditUser] = useState<EditVendorModal>({ ...defaultEditVendorModal });
  const [linkUser, setLinkUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { openSnackBar } = useSnackbar();
  const { user, hasImpersonateVendorAuth } = useAuth();

  const loadData = useCallback(() => {
    setIsLoading(true);
    VendorApi.getUsersByVendorNumber(vendor?.numberString)
      .then((result) => {
        setData(result);
      })
      .catch((err) => {
        openSnackBar(`Failed to load users ${err}`, 'error', 10000);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [vendor?.numberString, openSnackBar]);

  useEffect(() => {
    loadData();
  }, [vendor?.numberString, loadData]);

  const enterEdit = (u: User) => {
    let modalUser: EditVendorModal = {
      companyUserId: u.id,
      username: u.name,
      email: u.email,
      isActive: u.isActive,
      role: u.vendorPortalRole,
      newPassword: '',
      vendorName: vendor.name,
      vendorNumber: vendor.numberString,
      isNewUser: false,
    };

    setEditUser(modalUser);
    setOpenForm(true);
  };

  const addNewUser = () => {
    setEditUser({ ...defaultEditVendorModal, vendorName: vendor.name, vendorNumber: vendor.numberString, isNewUser: true });
    setOpenForm(true);
  };

  const vendorEditSubmit = (modalValues: EditVendorModal) => {
    setIsLoading(true);
    post<User>('/UserAdmin/upsertUser', { ...modalValues, vendorName: vendor.name, vendorNumber: vendor.numberString })
      .then((usr) => {
        loadData();
        openSnackBar(`${usr.name} ${modalValues.isNewUser ? 'Added' : 'Updated'}`);

        if (modalValues.isNewUser) {
          if (usr.welcomeEmailSent === true) {
            openSnackBar(`${usr.name} Added and Welcome email sent to ${usr.email}`);
          } else {
            openSnackBar(`${usr.name} Added Successfully, but Welcome email NOT sent to ${usr.email}, there was an error`, 'warning', 30000);
          }
        } else {
          if (usr.welcomeEmailSent === true) {
            openSnackBar(`${usr.name} Updated and Welcome email sent to ${usr.email}`);
          } else {
            openSnackBar(`${usr.name} Updated Successfully, but Welcome email NOT sent to ${usr.email}, there was an error`, 'warning', 30000);
          }
        }
      })
      .catch((err) => {
        openSnackBar(`Failed to save user ${err}`, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });

    setOpenForm(false);
  };

  const onVendorLinkClick = (u: User) => {
    setLinkUser(u);
    setOpenLinkUser(true);
  };

  const addNewLinkedVendorToUser = (newVendor: Vendor, u: User) => {
    setIsLoading(true);
    post<number>('/UserAdmin/linkUserToVendor', { vendorNumber: newVendor.numberString, companyUserId: u.id })
      .then(() => {
        loadData();
        openSnackBar(`User Linked to ${newVendor.name}`);
      })
      .catch((err) => {
        openSnackBar(`Failed to link user ${err}`, 'error');
      })
      .finally(() => {
        setIsLoading(false);
      });

    setOpenLinkUser(false);
  };

  const MyUsernameCell = (props: GridCellProps | any) => {
    const u = props.dataItem as User;
    return (
      <td>
        <span>{u.name}</span>
      </td>
    );
  };
  const MyEditCommandCell = (props: GridCellProps) => <EditCommandCell {...props} enterEdit={enterEdit} />;
  const MyLinkUserCommandCell = (props: GridCellProps | any) => <LinkUserCommandCell {...props} onVendorLinkClick={onVendorLinkClick} />;
  const MyVendorCell = (props: GridCellProps) => <VendorCell {...props} />;

  const LinkUserCommandCell = (props: GridCellProps | any) => {
    return (
      <td>
        <button className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary" onClick={() => props.onVendorLinkClick(props.dataItem)}>
          Link User
        </button>
      </td>
    );
  };

  const EditCommandCell = (props: GridCellProps | any) => {
    return (
      <td style={{ textAlign: 'center' }}>
        <Button variant="outlined" onClick={() => props.enterEdit(props.dataItem)}>
          Edit
        </Button>
      </td>
    );
  };

  const VendorCell = (props: GridCellProps) => {
    const u = props.dataItem as User;
    // return an un orderless list of vendors with name - number. If the name is greater than 15 characters, truncate it.
    return (
      <td style={{ padding: '0px' }}>
        <ul style={{ margin: '0px' }}>
          {u.vendors.map((v) => (
            <li key={v.numberString}>
              {v.name.length > 15 ? `${v.name.substring(0, 15)}...` : v.name} - {v.numberString}
            </li>
          ))}
        </ul>
      </td>
    );
  };

  const cancelLinkVendorDialog = () => {
    setOpenLinkUser(false);
    loadData();
  };

  return (
    <>
      {!isLoading && (
        <ShowTour
          id="userAdminTour"
          steps={[
            {
              target: '.add-new-user-btn',
              content: 'Add a new user, here.',
              disableBeacon: true,
              locale: { skip: 'Close Tour' },
            },
            {
              target: '.hover-cell-button',
              content: 'Hover over a row to edit',
              disableBeacon: true,
              locale: { skip: 'Close Tour', last: 'End Tour' },
            },
          ]}
        />
      )}
      <VendorEditForm cancelEdit={() => setOpenForm(false)} onSubmit={vendorEditSubmit} isOpen={openForm} user={editUser} />

      <LinkVendorDialog isOpen={openLinkUser} cancel={cancelLinkVendorDialog} onSubmit={addNewLinkedVendorToUser} user={linkUser} />

      <Button sx={{ mb: 3 }} variant="contained" onClick={addNewUser} className="add-new-user-btn">
        Add new
      </Button>

      <Box
        sx={{
          overflowX: 'auto',
          position: 'relative',
        }}
      >
        {isLoading && (
          <CircularProgress sx={{ position: 'absolute', mt: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9 }} size="1.5rem"></CircularProgress>
        )}

        <Grid
          data={orderBy(data, sort)}
          total={data.length}
          sortable={true}
          sort={sort}
          id="userAdminGrid"
          key={vendor.numberString}
          onSortChange={(e: GridSortChangeEvent) => {
            setSort(e.sort);
          }}
          resizable={false}
          style={{ minWidth: '900px' }}
          onRowDoubleClick={(e) => enterEdit(e.dataItem)}
        >
          {/* <GridColumn field="name" title="Username" /> */}
          <GridColumn field="name" title="Username" cell={MyUsernameCell} width="200" />
          <GridColumn field="email" title="Email" width="200" />
          <GridColumn field="vendorPortalRole" title="Role" width="75" />
          <GridColumn field="activatedDate" title="Activated Date" cell={DateCell} width="125" />
          <GridColumn field="isActive" title="IsActive" width="90" />
          <GridColumn field="passwordUpdateDate" title="Last Pwd Update" cell={DateCell} width="145" />
          {(user?.vendors?.length > 1 || hasImpersonateVendorAuth()) && <GridColumn field="vendors" title="Linked Vendors" cell={MyVendorCell} width="250" />}
          {(user?.vendors?.length > 1 || hasImpersonateVendorAuth()) && <GridColumn cell={MyLinkUserCommandCell} width="100" />}
          <GridColumn cell={MyEditCommandCell} width="100" />
          <GridColumn field="" title="" editable={false} />
        </Grid>
      </Box>
    </>
  );
};

export default UserAdminGrid;
